import { HIGHLIGHTS, tokenizeRoute } from 'query/routes';
import { getCsrfToken } from 'query';
import { getReviewId } from 'query/review/Review';

export type HighlightsResult = {
  includedKeywords: string[];
  excludedKeywords: string[];
};

export type HighlightType = 'included' | 'excluded';

const requireReviewId = () => {
  const reviewId = getReviewId();
  if (!reviewId)
    throw new Error("Couldn't get Review ID. Is this part of a review?");
  return reviewId;
};

const toHighlightsResult = (res: {
  highlight?: { included_keywords: string[]; excluded_keywords: string[] };
}): HighlightsResult => ({
  includedKeywords: res.highlight?.included_keywords ?? [],
  excludedKeywords: res.highlight?.excluded_keywords ?? [],
});

// Responses carry the review's full lists, so they must be applied in issue order.
let pendingMutation: Promise<unknown> = Promise.resolve();
const serialized = <T>(request: () => Promise<T>): Promise<T> => {
  const next = pendingMutation.then(request, request);
  pendingMutation = next.catch(() => undefined);
  return next;
};

// A hung request must not block the mutations queued behind it.
const MUTATION_TIMEOUT_MS = 30_000;
const withDeadline = async <T>(
  request: (signal: AbortSignal) => Promise<T>
): Promise<T> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), MUTATION_TIMEOUT_MS);
  try {
    return await request(controller.signal);
  } finally {
    clearTimeout(timer);
  }
};

export const getHighlights = async (): Promise<HighlightsResult> => {
  const url = tokenizeRoute(HIGHLIGHTS, { review_id: requireReviewId() });

  return fetch(url.toString(), {
    method: 'GET',
    headers: { Accept: 'application/json' },
  })
    .then((res) => res.json())
    .then(toHighlightsResult);
};

export const createHighlight = async (
  type: HighlightType,
  keyword: string
): Promise<HighlightsResult> =>
  serialized(() =>
    withDeadline(async (signal) => {
      const url = tokenizeRoute(HIGHLIGHTS, { review_id: requireReviewId() });

      const res = await fetch(url.toString(), {
        signal,
        method: 'POST',
        body: JSON.stringify({ highlight: { type, keyword } }),
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
      });

      if (!res.ok) throw new Error(`Failed to add highlight: ${res.status}`);

      return toHighlightsResult(await res.json());
    })
  );

export const deleteHighlight = async (
  type: HighlightType,
  keyword: string
): Promise<HighlightsResult> =>
  serialized(() =>
    withDeadline(async (signal) => {
      const url = tokenizeRoute(HIGHLIGHTS, { review_id: requireReviewId() });

      const res = await fetch(url.toString(), {
        signal,
        method: 'DELETE',
        body: JSON.stringify({ highlight: { type, keyword } }),
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
      });

      if (!res.ok) throw new Error(`Failed to remove highlight: ${res.status}`);

      return toHighlightsResult(await res.json());
    })
  );
